import type { UnifiedGroupBasicData } from '../../models';
import type {
    PersonaType,
    EmailAddressWrapper,
    PhoneNumberAttributedValue,
    EmailAddressAttributedValue,
    AttributionType,
} from '../../../service';
import {
    HubPersona,
    Person,
    LinkedContact,
    makeHubPersonaId,
    makeHubGroupPersonaId,
    PersonType,
    WellKnownNetworkNames,
} from '../..';
import {
    retrieveEmailAddressForSource,
    getDisplayNameForSource,
    getPersonaCoinNameForSource,
    shouldShowAttribution,
    getLinkedContactSource,
} from './hubPersonaFactoryAttributionUtils';

export function createHubPersona(personaType: PersonaType): HubPersona {
    const personaId = personaType.PersonaId?.Id;

    if (personaType.PersonaTypeString === 'ModernGroup') {
        const groupBasicData: UnifiedGroupBasicData = {
            type: PersonType.GroupBasicData,
            personaId,
            emailAddress: personaType.EmailAddress.EmailAddress,
            externalDirectoryObjectId: personaType.ExternalDirectoryObjectId,
            displayName: personaType.DisplayName,
            hubPersonaId: makeHubGroupPersonaId(
                personaType.ExternalDirectoryObjectId,
                personaType.EmailAddress.EmailAddress
            ),
            relevancyScore: personaType.RelevanceScore,
        };
        return groupBasicData;
    }

    if (personaType.PersonaTypeString === 'DistributionList') {
        // ItemId of DL is attribute of EmailAddressWrapper.
        // Note: PDLs in Public Folders do not have ItemId set for EmailAddress, so using personaId instead.
        const itemId = personaType.EmailAddress?.ItemId?.Id || personaId;

        const hubPersonaId = makeHubPersonaId(personaId);
        const listOriginalDisplayName = personaType.DisplayName;
        const displayNameFirstLast = personaType.DisplayNameFirstLast || listOriginalDisplayName;
        const displayNameLastFirst = personaType.DisplayNameLastFirst || listOriginalDisplayName;
        const relevancyScore = personaType.RelevanceScore;
        const creationTime = personaType.CreationTimeString;

        if (
            personaType.ADObjectId &&
            personaType.EmailAddress &&
            personaType.EmailAddress.MailboxType === 'PublicDL'
        ) {
            return {
                type: PersonType.PublicDistributionList,
                itemId,
                hubPersonaId,
                personaId,
                emailAddress: personaType.EmailAddress.EmailAddress,
                info: {
                    displayNameFirstLast,
                    displayNameLastFirst,
                    listOriginalDisplayName,
                },
                relevancyScore,
                creationTime,
            };
        }

        return {
            type: PersonType.PrivateDistributionList,
            itemId,
            hubPersonaId,
            personaId,
            info: {
                displayNameFirstLast,
                displayNameLastFirst,
                listOriginalDisplayName,
            },
            relevancyScore,
            members: [],
            creationTime,
        };
    }

    const originalDisplayName = fixEmptyDisplayName(personaType.DisplayName, personaType);

    const emailsArrays = [
        personaType.Emails1Array,
        personaType.Emails2Array,
        personaType.Emails3Array,
    ];

    let allEmailAddresses = emailAddressesUsingLokiLogic(
        personaType.EmailAddresses,
        personaType.AttributionsArray,
        emailsArrays
    ).map(emailAddress => getEmailAddressNameOrAddress(emailAddress, originalDisplayName));

    const mainEmailAddress =
        (allEmailAddresses && allEmailAddresses.length > 0 && allEmailAddresses[0]) ||
        personaType.EmailAddress?.EmailAddress;

    const canEditContact = personaType.EffectiveRights?.Modify;
    const canDeleteContact = personaType.EffectiveRights?.Delete;

    if (personaType.PersonaTypeString === 'Person') {
        const personaWithoutEmail: Person = {
            type: PersonType.Person,
            hubPersonaId: makeHubPersonaId(personaId),
            personaId: personaId,
            info: {
                allEmailAddresses: [],
                company: personaType.CompanyName,
                displayNameFirstLast: fixEmptyDisplayName(
                    personaType.DisplayNameFirstLast,
                    personaType
                ),
                displayNameLastFirst: fixEmptyDisplayName(
                    personaType.DisplayNameLastFirst,
                    personaType
                ),
                prefix: personaType.DisplayNamePrefix,
                surname: personaType.Surname,
                givenName: personaType.GivenName,
                generation: personaType.Generation,
                originalDisplayName,
                phoneNumber: getPhoneNumber(personaType),
                imAddress: personaType.ImAddress,
                GALContactGuid: personaType.ADObjectId,
                externalDirectoryObjectId: personaType.ExternalDirectoryObjectId,
                title: personaType.Title,
                department: personaType.Department,
                location: personaType.Location,
                homeCity: personaType.HomeCity,
                workCity: personaType.WorkCity,
            },
            linkedContacts: getLinkedContacts(personaType),
            totalNumberOfLinkedContacts: getTotalNumberOfLinkedContacts(personaType),
            isReadOnly: isReadOnly(personaType),
            relevancyScore: personaType.RelevanceScore,
            isjsMVVMFavorite: personaType.IsFavorite,
            creationTime: personaType.CreationTimeString,
            canEditContact,
            canDeleteContact,
        };

        if (!personaType.EmailAddress) {
            return personaWithoutEmail;
        }

        return {
            ...personaWithoutEmail,
            info: {
                ...personaWithoutEmail.info,
                mainEmailAddress: mainEmailAddress,
                allEmailAddresses: allEmailAddresses,
            },
            hasRogueRecipientCacheContact: hasRogueRecipientCacheContact(
                personaType.AttributionsArray,
                emailsArrays
            ),
        };
    }

    // Return a generic persona
    return {
        type: PersonType.Generic,
        hubPersonaId: makeHubPersonaId(personaId),
        personaId: personaId,
        info: {
            mainEmailAddress: mainEmailAddress,
            allEmailAddresses: allEmailAddresses,
            displayName: personaType.DisplayName,
            displayNameFirstLast: fixEmptyDisplayName(
                personaType.DisplayNameFirstLast,
                personaType
            ),
            displayNameLastFirst: fixEmptyDisplayName(
                personaType.DisplayNameLastFirst,
                personaType
            ),
            phoneNumber: getPhoneNumber(personaType),
            imAddress: personaType.ImAddress,
            GALContactGuid: personaType.ADObjectId,
            externalDirectoryObjectId: personaType.ExternalDirectoryObjectId,
            location:
                personaType.Location ||
                (personaType.OfficeLocationsArray &&
                    personaType.OfficeLocationsArray.length > 0 &&
                    personaType.OfficeLocationsArray[0].Value),
        },
        creationTime: personaType.CreationTimeString,
        relevancyScore: personaType.RelevanceScore,
        linkedContacts: getLinkedContacts(personaType),
        totalNumberOfLinkedContacts: getTotalNumberOfLinkedContacts(personaType),
    };
}

/**
 * Returns the email addresses mimicking the order and logic used in EV/Loki. The emails for each linked
 * contact are returned sorted on create date of the contact, in descending order. The latest email address
 * should thefore be the first email address in the list.
 *
 * As we don't have access to the create date of each contact, we cannot correctly detect the sequence when
 * contacts are linked across folders. Email addresses from recipient cache are ignored. This stops the issue of rogue
 * recipient cache contacts to surface.
 *
 * @param emailWrappers An array of EmailAddressWrapper elements to sort
 * @param attributionsArray An array holding the various contact sources
 * @param attributedEmailArrays An array of arrays holding information about each email address and its sources
 */
function emailAddressesUsingLokiLogic(
    emailWrappers: EmailAddressWrapper[],
    attributionsArray: AttributionType[],
    attributedEmailArrays: EmailAddressAttributedValue[][]
): EmailAddressWrapper[] {
    if (!emailWrappers) {
        return [];
    }

    if (emailWrappers.length === 1) {
        return emailWrappers;
    }

    // A map of attribution ids that are from recipient cache
    const isAttributionIdFromRecipientCache: { [id: string]: boolean } = {};

    attributionsArray &&
        attributionsArray.forEach(attribution => {
            if (attribution.DisplayName === WellKnownNetworkNames.RecipientCache) {
                isAttributionIdFromRecipientCache[attribution.Id] = true;
            }
        });

    // A map of good email addresses. A good email address is an email address that exists for
    // a non-recipient cache contact. If test@test.com only exists in a recipient cache contact,
    // then the address is not good. If test@test.com exists in a recipient cache contact and in one
    // or more explicit contacts or GAL contacts, then it's a good email address.
    const isGoodEmailAddress: { [email: string]: boolean } = {};

    attributedEmailArrays
        .filter(emailsArray => !!emailsArray)
        .forEach(emailsArray => {
            emailsArray.forEach(email => {
                email.Attributions.forEach(attributionId => {
                    let emailAddress =
                        email.Value?.EmailAddress && email.Value.EmailAddress.toLowerCase();
                    if (!!emailAddress) {
                        if (!isAttributionIdFromRecipientCache[attributionId]) {
                            isGoodEmailAddress[emailAddress] = true;
                        }
                    }
                });
            });
        });

    // Emails that have an ItemId
    const emailWrappersWithId: EmailAddressWrapper[] = [];

    // Emails without an ItemId could be from GAL or from recipient cache
    let emailWrappersWithoutId: EmailAddressWrapper[] = [];

    let prevId: string = undefined;
    let currIndex: number = 0;

    // Legacy FindPeople returns emails sorted on contact create date, in increasing order, but grouped
    // by folder. We want the sort order to be in decreasing order, so the last created contact is listed first.
    // Each contact can have 3 email addresses. These are listed in normal order. Due to the grouping by
    // folder, it is not possible to know the correct sort order if the contacts are in multiple folders.
    emailWrappers.forEach(emailWrapper => {
        const currId = emailWrapper.ItemId?.Id;
        if (currId) {
            if (
                emailWrapper.EmailAddress &&
                isGoodEmailAddress[emailWrapper.EmailAddress.toLowerCase()]
            ) {
                if (currId !== prevId) {
                    emailWrappersWithId.unshift(emailWrapper);
                    currIndex = 0;
                    prevId = currId;
                } else {
                    emailWrappersWithId.splice(++currIndex, 0, emailWrapper);
                }
            }
        } else {
            emailWrappersWithoutId.push(emailWrapper);
        }
    });

    return emailWrappersWithoutId.concat(emailWrappersWithId);
}

/**
 * The OWS FindPeople API returns an empty DisplayName property if none of the name properties are set, while
 * the DisplayNameFirstLast and DisplayNameLastFirst properties contain values, like phone number or email address.
 * For a persona with the Company property set, we want to override the DisplayName properties so the company name
 * is shown instead of the phone number or email address.
 * It's possible to have DisplayName empty but DisplayNameFirstLast to be correct and not email/phone,
 * since it might be created from Surname, GiveName or MiddleName.
 * @param value The value of the display name property
 * @param persona The persona to inspect
 */
export function fixEmptyDisplayName(value: string, personaType: PersonaType): string {
    if (personaType.DisplayName) {
        return value;
    } else if (
        (personaType.GivenName || personaType.Surname || personaType.MiddleName) &&
        personaType.DisplayNameFirstLast
    ) {
        return personaType.DisplayNameFirstLast;
    }

    if (personaType.CompanyName) {
        return personaType.CompanyName;
    }

    return value;
}

/**
 * - The OWS FindPeople API returns an X500 address for some contacts. Use the OriginalDisplayName in those cases.
 * - If a persona was created with an email address in an invalid format (E.g.: foobar@example), then the EmailAddressWrapper
 *   from the GetPersona response will have an empty EmailAdress field. In that case, we return the OriginalDisplayName,
 *   which will be populated with the inalid format address.
 * - For some cases the emailAddress.info.originalDisplayName was equal to EmailAddress and was X500 address,
 *   for them we want to use Persona.info.originalDisplayName
 */
export function getEmailAddressNameOrAddress(
    emailAddress: EmailAddressWrapper,
    personaOriginalDisplayName: string
): string {
    if (!emailAddress) {
        return undefined;
    }

    return emailAddress.RoutingType && emailAddress.RoutingType.toUpperCase() === 'EX'
        ? emailAddress.OriginalDisplayName === emailAddress.EmailAddress
            ? personaOriginalDisplayName
            : emailAddress.OriginalDisplayName
        : emailAddress.EmailAddress || emailAddress.OriginalDisplayName;
}

/**
 * Returns the first phone number in the attribution array
 * @param values An attribution array containing phone numbers
 */
function getPhoneNumberFromArray(values: PhoneNumberAttributedValue[]): string {
    return values && values.length > 0 ? values[0].Value.Number : null;
}

/**
 * Returns the phone number to display in the persona list for a persona. The type of number
 * to pick will probably be different for consumer and enterprise.
 * @param persona The persona
 */
function getPhoneNumber(personaType: PersonaType): string {
    const mobilePhoneNumber = getPhoneNumberFromArray(personaType.MobilePhonesArray);
    if (mobilePhoneNumber) {
        return mobilePhoneNumber;
    }

    const homePhoneNumber = getPhoneNumberFromArray(personaType.HomePhonesArray);
    if (homePhoneNumber) {
        return homePhoneNumber;
    }

    const businessPhoneNumber = getPhoneNumberFromArray(personaType.BusinessPhoneNumbersArray);
    if (businessPhoneNumber) {
        return businessPhoneNumber;
    }

    return null;
}

/**
 * Gets total number of linked contacts for a persona based on the AttributionsArray field.
 * The value can differ from length of getLinkedContacts, where some contact sources are filtered.
 * @param persona The Personatype to get linked contacts from
 */
function getTotalNumberOfLinkedContacts(personaType: PersonaType): number {
    return personaType.AttributionsArray
        ? personaType.AttributionsArray.filter(contact => contact.IsHidden === false).length
        : 0;
}

/**
 * Gets linked contacts from a persona based on the AttributionsArray field.
 * @param persona The Personatype to get linked contacts from
 */
function getLinkedContacts(personaType: PersonaType): LinkedContact[] {
    if (!personaType.AttributionsArray) {
        return [];
    }

    // This logic, albeit scattered in several methods,
    // can be found in Chicago: sources\Dev\owa\src\Client\Core\People\Core\Models\Persona.cs
    const visibleSources = personaType.AttributionsArray.filter(shouldShowAttribution);

    return visibleSources
        .map(source => {
            const email = retrieveEmailAddressForSource(personaType, source.Id);

            return {
                attributionId: source.Id,
                displayName: getDisplayNameForSource(personaType, source.Id, email),
                personaCoinName: getPersonaCoinNameForSource(personaType, source.Id),
                email,
                sourceDisplayName: getLinkedContactSource(source.DisplayName),
                sourceFolderName: source.FolderName,
                sourceId: source.SourceId.Id,
                sourceFolderId: source.FolderId?.Id,
                sourceChangeKey: source.SourceId.ChangeKey,
                containerPersonaId: personaType.PersonaId?.Id,
                isWritable: source.IsWritable,
            };
        })
        .reverse();
}

/**
 * Returns true if there are only read-only contacts in the personaType, false if not.
 * @param personaType The PersonaType to check for read-only state
 */
function isReadOnly(personaType: PersonaType): boolean {
    if (!personaType.AttributionsArray) {
        return true;
    }

    return personaType.AttributionsArray.every(contact => contact.IsWritable === false);
}

/**
 * Returns true if the persona has a rogue Recipient Cache contact, false if not. A contact from the Recipient Cache with an email
 * address that doesn't match any of the email addresses for the other non-Recipient Cache contacts in the persona is called a
 * rogue Recipient Cache contact. We want to log the number personas with such contacts, to better know how big the problem is.
 * There should ideally never be any rogue Recipient Cache contacts.
 * @param attributionsArray The attributions array for the persona
 * @param emailsArray The emails for the persona, with attribution references
 */
function hasRogueRecipientCacheContact(
    attributionsArray: AttributionType[],
    emailsArrays: EmailAddressAttributedValue[][]
): boolean {
    if (!emailsArrays) {
        return false;
    }

    const isFromRecipientCache = {};
    attributionsArray &&
        attributionsArray.forEach(
            attribution =>
                (isFromRecipientCache[attribution.Id] =
                    attribution.DisplayName === WellKnownNetworkNames.RecipientCache)
        );

    const recipientCacheEmails: string[] = [];
    const explicitContactEmails: string[] = [];

    // Build one array of email address from Recipient Cache contacts and one array of email address from non-Recipient Cache contacts
    emailsArrays
        .filter(emailsArray => !!emailsArray)
        .forEach(emailsArray => {
            emailsArray.forEach(email => {
                email.Attributions.forEach(attributionId => {
                    if (!!email.Value.EmailAddress) {
                        if (isFromRecipientCache[attributionId]) {
                            recipientCacheEmails.push(email.Value.EmailAddress.toLowerCase());
                        } else {
                            explicitContactEmails.push(email.Value.EmailAddress.toLowerCase());
                        }
                    }
                });
            });
        });

    // Verify that all email addresses for Recipient Cache contacts have a matching email address in
    // one of the non-Recipient cache contacts. If not, the contat is a rogue Recipient Cache contact.
    for (let i = 0; i < recipientCacheEmails.length; i++) {
        if (!explicitContactEmails.includes(recipientCacheEmails[i])) {
            return true;
        }
    }

    return false;
}
